import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Provider } from '@/pages/pay';
import { Button, P, Label, Spinner } from '@/components/index';
import { sandboxProviderCredentials } from '@/utils/sandbox-data';
import { LabelLowerCase } from './Typography';
import searchIcon from '../assets/icons/searchIcon.svg';
import SingleBank from './SingleBank';
import BankListItem from './BankListItem';
import Dropdown, { Item } from './CountryDropdown';

const CardWrapper = styled.section``;

const CardLabel = styled(LabelLowerCase)`
  margin-bottom: var(--space-2);
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 15px 0;
`;

const InputSection = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 2fr;
  align-items: center;
  border: 1px solid #dbe3eb;
  border-radius: 5px;
  margin-bottom: 20px;
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const InputIcon = styled.img`
  position: absolute;
  left: -3px;
  top: 8px;
  padding: 2px 5px;
  border-left: solid 1px #dbe3eb;
  @media (max-width: 500px) {
    border: none;
    left: 10px;
  }
`;

const Search = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: var(--space-2) 0 var(--space-2) 2.5rem;
  background-color: #f4f7f9;
  border: none;
  min-height: 20px;
  border-radius: 0 5px 5px 0;
  box-shadow: 0 9px 20px rgba(129, 129, 165, 0.05);
  font-family: inherit;
  font-size: 16px;
  color: var(--light-text);
  outline: none;
  @media (max-width: 500px) {
    border-top: solid 1px #dbe3eb;
    border-radius: 0 0 5px 5px;
    padding-left: 3.3rem;
  }
`;

const Banks = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.5rem;
`;

const SandboxCredentialList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  border: 1px solid #13273f20;
  border-radius: 10px;
  padding: 15px;
  width: 100%;
`;

/* const Banner = styled.div`
  background-color: #19385e;
  border-radius: 10px;
  padding: var(--space-2);
  margin-bottom: var(--space-2);
`;

const BannerText = styled(SmallP)`
  color: white;
`; */

interface BankSelectProps {
  activeBank: Provider;
  setActiveBank: (value: Provider) => void;
  providerList: Array<Provider>;
  loading?: boolean;
  isSandbox?: boolean;
  onRetryLoadProviders?: () => void;
  onCountryChange: React.Dispatch<React.SetStateAction<Item>>;
  country: Item;
  labelText?: string;
  responsiveLabelText?: string;
  changeButtonHandler?: () => void;
}

const BankSelect: React.FunctionComponent<BankSelectProps> = ({
  activeBank,
  setActiveBank,
  providerList,
  loading,
  isSandbox,
  onRetryLoadProviders,
  onCountryChange,
  country,
  labelText,
  responsiveLabelText,
  changeButtonHandler,
}) => {
  const [filteredProviderList, setFilteredProviderList] = useState([]);
  const [search, setSearch] = useState<string>(``);
  const [countryItem, setCountryItem] = useState<Item>(country);

  useEffect(() => {
    setFilteredProviderList(providerList);
  }, [providerList]);

  useEffect(() => {
    setCountryItem(country);
  }, [country]);

  const handleBankClick = (name: string) => () => {
    setActiveBank(providerList.find((item) => item.name === name));
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setFilteredProviderList(
      providerList.filter((item) =>
        item.name.toLowerCase().includes(event.target.value.toLowerCase()),
      ),
    );
  };

  const responsiveElement =
    typeof window !== `undefined` && window.innerWidth < 900;

  if (loading)
    return (
      <CardWrapper>
        <ButtonWrapper>
          <Spinner height={30} width={30} />
        </ButtonWrapper>
      </CardWrapper>
    );

  if (!loading && providerList?.length === 0)
    return (
      <CardWrapper>
        <ButtonWrapper>
          <div
            style={{
              display: `flex`,
              flexDirection: `column`,
              alignItems: `center`,
              justifyContent: `center`,
            }}
          >
            <P style={{ marginBottom: 20 }}>
              Something went wrong while loading available providers, please
              retry
            </P>
            <Button white onClick={onRetryLoadProviders}>
              Retry
            </Button>
          </div>
        </ButtonWrapper>
      </CardWrapper>
    );

  return (
    <CardWrapper>
      {/* activeBank && (
        <Banner>
          <BannerText>
            We are redirecting you to your bank. You&#39;ll be sent back here
            after you confirm your deposit
          </BannerText>
        </Banner>
      ) */}
      {!activeBank &&
        (responsiveElement ? (
          <CardLabel>
            {responsiveLabelText ||
              `We'll automatically send you to your bank to approve the fast and secure payment`}
          </CardLabel>
        ) : (
          <CardLabel>{labelText || `Pay with online banking.`} </CardLabel>
        ))}
      {!activeBank && (
        <InputSection>
          <Dropdown
            value={countryItem}
            items={[{ label: `United Kingdom`, value: `GB` }]}
            onChange={onCountryChange}
            disabled
          />
          <div style={{ width: `100%`, position: `relative` }}>
            <Search
              type="text"
              placeholder="Search all banks"
              value={search}
              onChange={handleSearch}
            />
            <InputIcon src={searchIcon} alt="input icon" />
          </div>
        </InputSection>
      )}
      {activeBank && (
        <>
          {isSandbox && (
            <SandboxCredentialList>
              <Label>
                You need to use the following credentials to make a sandbox
                payment:
              </Label>
              {sandboxProviderCredentials[activeBank.externalId].map((l) => (
                <P>{l}</P>
              ))}
            </SandboxCredentialList>
          )}
          <SingleBank onClick={changeButtonHandler} {...activeBank} />
        </>
      )}
      <Banks>
        {!activeBank &&
          filteredProviderList
            .sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            })
            .map((bank) => (
              <BankListItem
                key={bank.externalId}
                onClick={handleBankClick(bank.name)}
                {...bank}
              />
            ))}
      </Banks>
    </CardWrapper>
  );
};

export default BankSelect;
